import { useGreeting } from '../../ui/greeting';
import { useState, type ReactNode } from 'react';
import {
  scopeLabel,
  api,
  fmtClock,
  fmtCount,
  type AlertRule,
  type CaptureSummary,
  type EnabledFrameworks,
  frameworksEnabled,
  type FrameworksResponse,
  type MetricsMonitoring,
  type MetricsOverview,
  type OperatorFleet,
  type PulseAnomalies,
  type PulseFacilities,
  type PulseIncidents,
  type PulseSlo,
  type OverviewTriage,
  type RevenueIntegrity,
  fmtAgeSince,
  fmtMoneyCompact,
  overviewTodo,
  overviewTodoDone,
  type OverviewTodo,
  type TodoDone,
  guardianIncidentsNetwork,
  type GuardianIncident,
} from '@aithora/shared';
import { OperatorV9Tab, liveCount } from './operatorV9';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { narrowQuery, useFacilityScope, usePickedIds } from '../../session/FacilityScope';
import { sx } from '../../ui/designStyles';
import type { QueryState } from '../../useQuery';
import { roleChipText } from '../../shell/TopBar';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { shortName, useApi } from './common';
import { GoLiveLine } from './GoLiveLine';
import { OperatorTriage, unownedCount } from './OperatorTriage';
import { FleetTodo, type FleetGridData } from './TodoTab';
import {
  BaselineBlock,
  belowMedianCount,
  ComplianceBlock,
  GuardPostureBlock,
  HealthGrid,
  IncidentsBlock,
  MoneyNote,
  incidentNote,
  moneyOrder,
  notBilledOf,
  rankIncidents,
  worstFirst,
  ObjectivesRow,
  type MoneyRow,
  type Tile,
} from './operatorBlocks';

/** How many facilities the Facilities and Money blocks show (design: six, two columns). */
const SHOWN = 6;
const WINDOW_DAYS = 14;

/**
 * The design's twelve Needs action tiles, in its order, each only from a source that
 * answered. A source that failed, or a count no route serves yet, adds no tile.
 */
export function operatorTiles(src: {
  incidents: PulseIncidents | null;
  facilities: PulseFacilities | null;
  anomalies: PulseAnomalies | null;
  slo: PulseSlo | null;
  rules: AlertRule[] | null;
  overview: MetricsOverview | null;
  fleet: OperatorFleet | null;
  money: MoneyRow[] | null;
}): Tile[] {
  const t: Tile[] = [];
  const flags = src.overview?.flags;
  if (flags) t.push({ key: 'queue', short: 'Review queue', long: `Caregiver review queue, ${flags.queue} waiting`, n: flags.queue, sev: 'normal', to: '/worklist' });
  if (flags) t.push({ key: 'crisis', short: 'Crisis', long: `Crisis escalations, ${flags.crisis} in the window`, n: flags.crisis, sev: 'breach', to: '/guardian' });
  if (flags) t.push({ key: 'drift', short: 'AI checks drifting', long: `AI checks drifting from how they usually answer, ${flags.drift} in the window`, n: flags.drift, sev: 'normal', to: '/guardian' });
  if (src.fleet) {
    const n = belowMedianCount(src.fleet.facilities);
    t.push({ key: 'median', short: 'Below typical', long: `Facilities below the typical score for their peers, ${n} of ${src.fleet.facilities.length} graded`, n, sev: 'normal', to: '/performance' });
  }
  if (src.incidents) {
    const n = src.incidents.incidents.filter((i) => i.status === 'open').length;
    t.push({ key: 'unacked', short: 'Unacknowledged', long: `${n} System Pulse incidents nobody has acknowledged`, n, sev: 'breach', to: '/oversight' });
  }
  if (src.rules) {
    const n = src.rules.filter((r) => !r.enabled).length;
    t.push({ key: 'muted', short: 'Alerts switched off', long: `${n} alert rules switched off, not paging`, n, sev: 'risk', to: '/oversight' });
  }
  if (src.facilities) {
    const f = src.facilities.facilities;
    const noEsc = f.filter((x) => x.signals.some((s) => s.name === 'escalation' && s.status === 'unknown')).length;
    t.push({ key: 'noesc', short: 'No escalation path', long: `${noEsc} facilities have never used their escalation path: unknown, not healthy`, n: noEsc, sev: 'risk', to: '/oversight' });
    const unwired = f.filter((x) => x.status === 'unknown' || x.status === 'n/a').length;
    t.push({ key: 'unwired', short: 'Not connected', long: `${unwired} facilities with no signal: not healthy`, n: unwired, sev: 'risk', to: '/atlas' });
    const deg = f.filter((x) => x.status === 'degraded' || x.status === 'down').length;
    t.push({ key: 'degraded', short: 'Degraded', long: `${deg} facilities degraded or down`, n: deg, sev: 'breach', to: '/oversight' });
  }
  if (src.anomalies) {
    const n = new Set(src.anomalies.anomalies.filter((a) => a.scope === 'facility').map((a) => a.id)).size;
    t.push({ key: 'anomaly', short: 'Off their usual', long: `${n} facilities off their own usual (errors or slowest responses)`, n, sev: 'breach', to: '/oversight' });
  }
  if (src.money && src.money.length > 0 && src.money.every((r) => r.summary.filingWindow)) {
    const n = src.money.filter((r) => r.summary.filingWindow!.closesInDays <= 10).length;
    t.push({ key: 'windows', short: 'Filing windows', long: `Filing windows closing inside 10 days: ${n}`, n, sev: 'breach', to: '/finance', modeled: false });
  }
  if (src.slo) {
    const n = src.slo.slos.filter((s) => s.targetPct !== null && (s.status === 'warning' || s.status === 'critical')).length;
    t.push({ key: 'slos', short: 'Response targets missed', long: `${n} response targets missed`, n, sev: 'breach', to: '/oversight' });
  }
  return t;
}

/** Tiles in the design that no route counts yet, given what did answer. */
export function tilesNotCounted(overview: MetricsOverview | null, money: MoneyRow[] | null): string[] {
  const out: string[] = [];
  if (overview && !overview.flags) out.push('crisis escalations', 'AI checks drifting', 'caregiver review queue');
  if (money && !(money.length > 0 && money.every((r) => r.summary.filingWindow))) out.push('filing windows closing');
  return out;
}

/**
 * The lede (design ovLedeSub, L47270): "last 14 days · N facilities · M live". N is the fleet
 * list's length and M counts that same list, so every facility count on the page agrees.
 */
export function ledeSub(fleetCount: number | null, live: number | null): string {
  return `last ${WINDOW_DAYS} days${fleetCount !== null ? ` · ${fmtCount(fleetCount)} ${fleetCount === 1 ? 'facility' : 'facilities'}` : ''}${live !== null ? ` · ${fmtCount(live)} live` : ''}`;
}

function Lede({ overview, fleet, facilities }: { overview: QueryState<MetricsOverview>; fleet: QueryState<OperatorFleet>; facilities: QueryState<PulseFacilities> }) {
  const { session } = useSession();
  const { role } = useConsoleRole();
  const now = new Date();
  const hello = useGreeting(now);
  const conv = overview.data?.conversations;
  // Live: every link in the facility's safety chain reports (nothing unknown, nothing missing).
  const live = liveCount(fleet.data, facilities.data);
  return (
    <div className="op-lede">
      <span className="op-lede__left">
        <h2 className="op-lede__greet">{`${hello}.`}</h2>
        <span className="op-lede__meta" data-testid="op-lede-sub">
          {session ? roleChipText(session, role) : ''}
          {' · '}
          <span className="tnum">{fmtClock(now)}</span>
          {' · '}
          {ledeSub(fleet.data ? fleet.data.facilities.length : null, live)}
        </span>
      </span>
      <span className="op-lede__right">
        <span className="op-lede__row">
          <span className="op-lede__v">{conv ? fmtCount(conv.total) : '–'}</span>
          {conv && (conv.real ? <span className="op-live">Live</span> : <HonestyBadge flags={[{ real: false }]} figure="Conversations governed" />)}
        </span>
        {/* Atlas conversations started in the window (conversations table). Not
            "governed": a governed decision is one the gate recorded in the ledger, which
            Guard posture counts, and the two are different numbers. */}
        <span className="op-lede__k">Atlas conversations</span>
      </span>
    </div>
  );
}

/** "$4.2K" as a number (the design sorts Money on the table by what it prints). */
const printedAmount = (t: string) => {
  const n = parseFloat(t.replace(/[^0-9.]/g, '')) || 0;
  return /M/.test(t) ? n * 1e6 : /K/i.test(t) ? n * 1e3 : n;
};

/** The To do's Operator columns (design ovnFac, ovnMoney, ovnInc): the Overview's own lists, reordered. */
export function fleetGrid(fleet: OperatorFleet | null, money: MoneyRow[] | null, incidents: PulseIncidents | null, moneyNote: ReactNode, now: Date = new Date()): FleetGridData {
  return {
    facilities: fleet
      ? worstFirst(fleet.facilities).slice(0, SHOWN).map((f) => {
          const c = f.score.value;
          const t = f.trend?.value ?? null;
          const d = t === null ? null : Math.round(t);
          return {
            id: f.orgId, name: shortName(f.name), full: f.name, score: c === null ? (f.grade ?? '–') : String(Math.round(c)),
            delta: d === null ? null : d === 0 ? 'flat' : `${d > 0 ? '▲' : '▼'} ${Math.abs(d)}`, tone: d === null || d === 0 ? 'none' : d > 0 ? 'good' : 'act',
          };
        })
      : null,
    money: money
      ? money
          .map((r) => {
            const nb = notBilledOf(r.summary).value;
            const amount = fmtMoneyCompact(nb === null ? null : nb / 100);
            return { id: r.facility.orgId, name: shortName(r.facility.name), amount };
          })
          // Largest first by the amount as printed (the design's amt()), so equal amounts keep their order.
          .sort((a, b) => printedAmount(b.amount) - printedAmount(a.amount))
          .slice(0, 5)
      : null,
    moneyNote,
    incidents: incidents ? rankIncidents(incidents.incidents, now).slice(0, 4).map((i) => ({ id: i.id, what: i.title, note: incidentNote(i, now), age: fmtAgeSince(i.openedAt, now) })) : null,
  };
}

/**
 * The switcher picked a place with none of the buildings that report here (design geoLayer:
 * "No facilities in {place} report here yet", with Show all facilities).
 */
function NoneInView() {
  const { scope, setScope } = useFacilityScope();
  return (
    <div className="ov-empty" role="status" style={sx('display:flex;flex-direction:column;gap:8px;padding:18px 0;')}>
      <strong style={sx('font-size:15px;')}>No facilities in {scopeLabel(scope)} report here yet</strong>
      <span style={sx('font-size:13px;color:#54606C;')}>Facility health and performance come from the buildings connected to Aithora. None of them is in this view.</span>
      <button type="button" className="btn btn--ghost" style={sx('align-self:flex-start;')} onClick={() => setScope({})}>Show all facilities</button>
    </div>
  );
}

export function OperatorOverview() {
  const { session } = useSession();
  const hello = useGreeting();
  const { role } = useConsoleRole();
  // To do is Aithora's (Super Admin and Operator); Sales, Dev and the Partner keep Overview and Triage.
  const todoVariant = role === 'SUPER_ADMIN' ? 'super' : role === 'OPERATOR' ? 'operator' : null;
  const [tab, setTab] = useState<'overview' | 'triage' | 'todo'>('overview');
  const [queueOpen, setQueueOpen] = useState(false);
  const todo = useApi<OverviewTodo>((c, signal) => overviewTodo(c, { signal }), [], tab === 'todo' && !!todoVariant);
  const doneToday = useApi<TodoDone>((c, signal) => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return overviewTodoDone(c, { query: { since: midnight.toISOString() }, signal });
  }, [], tab === 'todo' && !!todoVariant);

  const fleet = useApi<OperatorFleet>((c, signal) => api.operatorFleet(c, { signal }));
  const overview = useApi<MetricsOverview>((c, signal) => api.metricsOverview(c, { query: { windowDays: WINDOW_DAYS }, signal }));
  const monitoring = useApi<MetricsMonitoring>((c, signal) => api.metricsMonitoring(c, { query: { windowHours: WINDOW_DAYS * 24 }, signal }));
  const posture = useApi((c, signal) => api.policyPosture(c, { query: { scope: 'network' }, signal }));
  const facilities = useApi<PulseFacilities>(async (c, signal) => (await api.pulseFacilities(c, { signal })).data);
  const slo = useApi<PulseSlo>(async (c, signal) => (await api.pulseSlo(c, { signal })).data);
  const incidents = useApi<PulseIncidents>(async (c, signal) => (await api.pulseIncidents(c, { query: { windowDays: 7 }, signal })).data);
  const anomalies = useApi<PulseAnomalies>(async (c, signal) => (await api.pulseAnomalies(c, { signal })).data);
  const rules = useApi<AlertRule[]>(async (c, signal) => (await api.pulseAlertRules(c, { signal })).data);
  // The facility switcher (V6): facilities, money, health, triage and revenue follow the pick;
  // the other cards show every facility (the page note says so).
  const pick = usePickedIds();
  const pickKey = pick ? pick.join(',') : '';
  const pickQuery = pick ? { facilities: pickKey } : undefined;
  const fleetIn = narrowQuery(fleet, pick, (d, keep) => ({ ...d, facilities: d.facilities.filter((f) => keep.has(f.orgId)) }));
  const facilitiesIn = narrowQuery(facilities, pick, (d, keep) => ({ ...d, facilities: d.facilities.filter((f) => keep.has(f.organizationId)) }));
  const triage = useApi<OverviewTriage>((c, signal) => api.overviewTriage(c, { query: pickQuery, signal }), [pickKey]);
  const revenue = useApi<RevenueIntegrity>((c, signal) => api.revenueIntegrity(c, { query: pickQuery, signal }), [pickKey], tab === 'triage');
  const golive = useApi((c, signal) => api.goLiveQueue(c, { signal }));
  // An unclaimed emergency leads the today line (design ovTodayLine), across every building.
  const emergencies = useApi<{ incidents: GuardianIncident[] }>((c, signal) => guardianIncidentsNetwork(c, { query: pickQuery, signal }), [pickKey], role === 'SUPER_ADMIN' || role === 'OPERATOR');
  const network = useApi<CaptureSummary>((c, signal) => api.captureSummary(c, { query: pickQuery, signal }), [pickKey]);
  const frameworks = useApi<FrameworksResponse>((c, signal) => api.frameworks(c, { signal }));
  const frameworksOn = useApi<EnabledFrameworks>((c, signal) => frameworksEnabled(c, { signal }));

  // Money rows: one capture summary per facility, soonest filing window first when the
  // route sends windows (else worst composite first), after the fleet lands.
  const ids = fleetIn.data ? fleetIn.data.facilities.map((f) => f.orgId).join(',') : '';
  const money = useApi<MoneyRow[]>(
    async (c, signal) => {
      const rows = fleetIn.data!.facilities;
      const out = await Promise.all(rows.map(async (facility) => ({ facility, summary: await api.captureSummary(c, { query: { facility: facility.orgId }, signal }) })));
      return moneyOrder(out.filter((r) => notBilledOf(r.summary).value !== null));
    },
    [ids],
    !!fleetIn.data,
  );
  const noneInView = !!pick && !!fleetIn.data && fleetIn.data.facilities.length === 0;
  const moneyState: QueryState<MoneyRow[]> = fleet.error && !fleet.data ? { ...money, error: fleet.error, loading: false } : { ...money, data: money.data ? money.data.slice(0, SHOWN) : null };

  const tiles = operatorTiles({
    incidents: incidents.data,
    facilities: facilities.data,
    anomalies: anomalies.data,
    slo: slo.data,
    rules: rules.data,
    overview: overview.data,
    fleet: fleet.data,
    money: money.data,
  });
  const tilesLoading = [incidents, facilities, anomalies, slo, rules, overview, fleet].some((x) => x.loading && !x.data);
  const unowned = unownedCount(triage.data);

  return (
    <div className="ov ov--operator op" data-testid="overview-operator">
      <div className="op-tabs">
        <div role="tablist" aria-label="Overview sections" className="op-tabs__list">
          <button role="tab" type="button" aria-selected={tab === 'overview'} className={`op-tab${tab === 'overview' ? ' op-tab--on' : ''}`} onClick={() => setTab('overview')}>
            Overview
          </button>
          <button role="tab" type="button" aria-selected={tab === 'triage'} className={`op-tab${tab === 'triage' ? ' op-tab--on' : ''}`} onClick={() => setTab('triage')}>
            Triage
            {!!unowned && <span className="op-tab__count">{unowned}</span>}
          </button>
          {todoVariant && (
            <button role="tab" type="button" aria-selected={tab === 'todo'} className={`op-tab${tab === 'todo' ? ' op-tab--on' : ''}`} onClick={() => setTab('todo')}>
              To do
            </button>
          )}
        </div>
        <GoLiveLine q={golive} open={queueOpen} onOpenChange={setQueueOpen} />
      </div>

      {tab === 'todo' && todoVariant ? (
        <FleetTodo
          variant={todoVariant}
          todo={todo}
          done={doneToday}
          golive={golive}
          greeting={`${hello}.`}
          tenantLabel={session ? roleChipText(session, role) : ''}
          clock={fmtClock(new Date())}
          lede={overview.data?.conversations ? { v: fmtCount(overview.data.conversations.total), k: 'Atlas conversations' } : null}
          grid={fleetGrid(fleet.data, money.data ? money.data.slice(0, SHOWN) : null, incidents.data, <MoneyNote network={network} />)}
          onOpenQueue={() => setQueueOpen((o) => !o)}
        />
      ) : tab === 'triage' ? (
        <OperatorTriage q={triage} revenue={revenue} incidents={incidents} />
      ) : (
        noneInView ? (
          <>
            <Lede overview={overview} fleet={fleetIn} facilities={facilitiesIn} />
            <NoneInView />
          </>
        ) : (
          <OperatorV9Tab
            lede={<Lede overview={overview} fleet={fleetIn} facilities={facilitiesIn} />}
            fleet={fleetIn}
            tiles={tiles}
            tilesLoading={tilesLoading}
            money={moneyState}
            network={network}
            overview={overview}
            incidents={emergencies.data?.incidents ?? null}
            onTriage={() => setTab('triage')}
            notCounted={tilesNotCounted(overview.data, money.data)}
            compliance={<ComplianceBlock q={frameworks} enabled={frameworksOn} />}
            details={
              <>
                {/* The V7 system blocks, unchanged, behind "Show system details" (design ovSys). */}
                <div className="op-cols op-cols--top">
                  <div className="op-col">
                    <IncidentsBlock q={incidents} overview={overview} rules={rules} facilities={facilities} anomalies={anomalies} />
                    <HealthGrid q={facilitiesIn} />
                  </div>
                  <div className="op-col op-col--right">
                    <GuardPostureBlock overview={overview} monitoring={monitoring} posture={posture} />
                    <BaselineBlock q={anomalies} facilities={facilities.data} />
                  </div>
                </div>
                <ObjectivesRow q={slo} />
              </>
            }
          />
        )
      )}
    </div>
  );
}
